import { useCallback, useEffect, useRef, useState } from 'react';
import { LoadInfiniteHook, useGetDataHook, useSaveDataHook } from '@bymason/api-hooks-base';
import {
  DeviceModel,
  devicesClient,
  GetDeviceOptions,
  GetDevicesOptions,
  NotifyDevicesOptions,
  RenameDeviceOptions,
  GetDevicePeripheralsOptions,
} from '@bymason/controller-api';

export {
  DeviceModel,
  DeviceStatus,
  DeviceLocation,
  GetDevicesOptions,
  NotifyType,
  NotificationType,
  DangrousNotificationType,
  BluetoothInfo,
} from '@bymason/controller-api';

/**
 * React hook that asynchronously gets paginated devices.
 * @returns HookArray
 * @HookArray [0] get method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useNotifyDevices(clientToken: string) {
  const handlePostData = useCallback(
    (options: NotifyDevicesOptions) => {
      return devicesClient.notifyDevices(options, clientToken);
    },
    [clientToken]
  );

  return useSaveDataHook(handlePostData);
}

/**
 * React hook that asynchronously gets paginated devices.
 * @returns HookArray
 * @HookArray [0] get method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useGetDevices(clientToken: string) {
  const handleGetData = useCallback(
    (options: GetDevicesOptions, signal?: AbortSignal | null) => {
      return devicesClient.getDevices(options, clientToken, signal);
    },
    [clientToken]
  );

  return useGetDataHook(handleGetData);
}

/**
 * React hook that asynchronously gets a device.
 * @returns HookArray
 * @HookArray [0] get method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useGetDevice(clientToken: string) {
  const handleGetData = useCallback(
    (options: GetDeviceOptions) => {
      return devicesClient.getDevice(options, clientToken);
    },
    [clientToken]
  );

  return useGetDataHook(handleGetData);
}

/**
 * React hook that asynchronously gets status info of a device.
 * @returns HookArray
 * @HookArray [0] get method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useGetDeviceStatus(clientToken: string) {
  const handleGetData = useCallback(
    (options: GetDeviceOptions) => {
      return devicesClient.getDeviceStatus(options, clientToken);
    },
    [clientToken]
  );

  return useGetDataHook(handleGetData);
}

/**
 * React hook that asynchronously gets location info of a device.
 * @returns HookArray
 * @HookArray [0] get method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useGetDeviceLocation(clientToken: string) {
  const handleGetData = useCallback(
    (options: GetDeviceOptions) => {
      return devicesClient.getDeviceLocation(options, clientToken);
    },
    [clientToken]
  );

  return useGetDataHook(handleGetData);
}

/**
 * React hook that asynchronously renames a device.
 * @returns HookArray
 * @HookArray [0] get method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useRenameDevice(clientToken: string) {
  const handleGetData = useCallback(
    (options: RenameDeviceOptions) => {
      return devicesClient.renameDevice(options, clientToken);
    },
    [clientToken]
  );

  return useGetDataHook(handleGetData);
}

/**
 * React hook that loads paginated devices "infinitely" via trigger,
 * for components that need to load more data on scroll or button click.
 * @returns HookArray
 * @HookArray [0] result
 * @HookArray [1] error
 * @HookArray [2] busy flag
 * @HookArray [3] page count
 * @HookArray [4] error code
 * @HookArray [5] clearData method
 */
export function useLoadInfiniteDevices(
  clientToken: string,
  options: GetDevicesOptions,
  loadMoreTrigger = false
) {
  const [getData, data, error, busy, errorCode, errorData, clearData] = useGetDevices(clientToken);
  const [loadedData, setLoadedData] = useState<DeviceModel[]>([]);
  const [hasMoreData, setHasMoreData] = useState(false);
  const [nextPageMarker, setNextPageMarker] = useState<string | undefined>();
  const blockTrigger = useRef(false);
  const pageCount = useRef(0);
  const controller = useRef<AbortController | null>(null);

  const resetLoadedData = useCallback(() => {
    clearData();
    setHasMoreData(false);
    setNextPageMarker(undefined);
    blockTrigger.current = false;
    pageCount.current = 0;
    controller.current = new AbortController();
    getData(options, controller.current.signal);
  }, [clearData, getData, options]);

  // Fetch initial data
  useEffect(() => {
    if (clientToken) {
      if (controller.current && !controller.current.signal.aborted) {
        controller.current.abort();
      }
      resetLoadedData();
    }
    // must have client token as dep. if it changes we
    // need to reset the loaded data
  }, [resetLoadedData, clientToken, clearData, getData, options]);

  // Fetch more data if conditions are right
  useEffect(() => {
    if (loadMoreTrigger && hasMoreData && !blockTrigger.current) {
      getData({ ...options, after: nextPageMarker });
    }
  }, [getData, options, loadMoreTrigger, hasMoreData, nextPageMarker]);

  // Process fetched data into loaded data and set flags
  useEffect(() => {
    if (!error && !busy) {
      const currData = data ? data.data : [];
      blockTrigger.current = true;
      pageCount.current++;

      setLoadedData((prevData) => {
        if (prevData.length <= 5000) {
          if (pageCount.current <= 1) {
            return currData;
          }
          return [...prevData, ...currData];
        }
        return prevData;
      });
      setHasMoreData(currData && !!data?.nextPage);
      setNextPageMarker(data?.nextPage);
    }

    if (error) {
      setHasMoreData(false);
    }
  }, [busy, data, error]);

  // Trigger block makes sure only one get event is triggered until the
  // trigger is reset. For instance, A scroll bottom trigger doesn't reset
  // to false after the data loads,but eventually does reset with the next onScroll event.
  useEffect(() => {
    if (loadMoreTrigger === false) {
      blockTrigger.current = false;
    }
  });

  return [
    loadedData,
    error,
    busy,
    pageCount.current,
    errorCode,
    errorData,
    resetLoadedData,
  ] as LoadInfiniteHook<DeviceModel[]>;
}

/**
 * React hook that asynchronously gets the peripheral list for a device.
 * @returns HookArray
 * @HookArray [0] get method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useGetDevicePeripherals(clientToken: string) {
  const handleGetData = useCallback(
    (options: GetDevicePeripheralsOptions) => {
      return devicesClient.getDevicePeripherals(options, clientToken);
    },
    [clientToken]
  );

  return useGetDataHook(handleGetData);
}
